import React from "react";
import {
  Box,
  Button,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  OktonAlert,
  OktonInput,
  OktonLoadingState,
  OktonSelect,
} from "@okton/platform-ui";
import {
  createPrice,
  decimal,
  prices,
  ProviderError,
  settings,
  validSource,
  whole,
} from "./aiProviderApi";
import { ProviderState, Tariff } from "./types";
const emptyPrice = {
  model: "",
  input: "",
  cache: "",
  output: "",
  fx: "",
  date: "",
  source: "",
};
type PriceFields = typeof emptyPrice;
export const AiProviderPanel: React.FC<{ accessToken?: string | null }> = ({
  accessToken,
}) => {
  const [state, setState] = React.useState<ProviderState | null>(null);
  const [catalog, setCatalog] = React.useState<Tariff[]>([]);
  const [documentation, setDocumentation] = React.useState("");
  const [conversation, setConversation] = React.useState("");
  const [limit, setLimit] = React.useState("");
  const [calls, setCalls] = React.useState("");
  const [output, setOutput] = React.useState("");
  const [price, setPrice] = React.useState<PriceFields>({ ...emptyPrice });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [requiresReload, setRequiresReload] = React.useState(false);
  const [reload, setReload] = React.useState(0);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const controllerRef = React.useRef<AbortController | null>(null);
  const savingRef = React.useRef(false);
  const apply = (data: ProviderState) => {
    setState(data);
    const c = data.configuration;
    setDocumentation(c.documentationTariffId || "");
    setConversation(c.conversationTariffId || "");
    setLimit((c.requestLimitCents / 100).toFixed(2).replace(".", ","));
    setCalls(String(c.maxCallsPerRequest));
    setOutput(String(c.maxOutputTokens));
  };
  React.useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    savingRef.current = false;
    setSaving(false);
    setState(null);
    setCatalog([]);
    setLoading(true);
    setError("");
    setMessage("");
    setRequiresReload(false);
    setPrice({ ...emptyPrice });
    if (!accessToken) {
      setLoading(false);
      setError("Sessão indisponível. Entre novamente no SSO Admin.");
      return () => controller.abort();
    }
    Promise.all([
      settings(accessToken, controller.signal),
      prices(accessToken, controller.signal),
    ])
      .then(([data, list]) => {
        if (!controller.signal.aborted) {
          apply(data);
          setCatalog(list);
        }
      })
      .catch((failure) => {
        if (!controller.signal.aborted)
          setError(
            failure instanceof Error
              ? failure.message
              : "Erro ao carregar configuração.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [accessToken, reload]);
  async function write(kind: "settings" | "price"): Promise<void> {
    const controller = controllerRef.current;
    if (
      !state ||
      !controller ||
      controller.signal.aborted ||
      !accessToken ||
      savingRef.current ||
      requiresReload
    )
      return;
    setError("");
    setMessage("");
    let operation: () => Promise<void>;
    if (kind === "settings") {
      const requestLimitCents = decimal(limit, 2, 100000000),
        maxCallsPerRequest = whole(calls, 100),
        maxOutputTokens = whole(output, 10000000);
      if (
        requestLimitCents === null ||
        maxCallsPerRequest === null ||
        maxOutputTokens === null
      ) {
        setError("Informe limites válidos, sem separador de milhar.");
        return;
      }
      operation = async () => {
        const saved = await settings(accessToken, controller.signal, {
          revision: state.configuration.revision,
          documentationTariffId: documentation || null,
          conversationTariffId: conversation || null,
          requestLimitCents,
          maxCallsPerRequest,
          maxOutputTokens,
        });
        if (!controller.signal.aborted) {
          apply(saved);
          setMessage(
            saved.executionStatus === "ready_for_sample"
              ? "Configuração salva. Preparado para amostra sintética; conexão ainda sujeita a teste."
              : "Configuração salva. O envio à OpenAI ainda não está habilitado.",
          );
        }
      };
    } else {
      const input = decimal(price.input, 6, 1000000000),
        cache = decimal(price.cache, 6, 1000000000),
        out = decimal(price.output, 6, 1000000000),
        fx = decimal(price.fx, 6, 1000000000);
      if (
        input === null ||
        input === 0 ||
        cache === null ||
        cache > input ||
        out === null ||
        out === 0 ||
        fx === null ||
        fx === 0 ||
        !/^[A-Za-z0-9._:-]{1,120}$/.test(price.model) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(price.date) ||
        !Number.isFinite(Date.parse(price.date)) ||
        new Date(price.date).toISOString().slice(0, 10) !== price.date ||
        !validSource(price.source)
      ) {
        setError(
          "Confira modelo, data, fonte oficial e valores com até seis casas decimais.",
        );
        return;
      }
      operation = async () => {
        const saved = await createPrice(accessToken, controller.signal, {
          provider: "openai",
          mode: "standard-text",
          model: price.model,
          inputMicrosPerMillion: input,
          cachedInputMicrosPerMillion: cache,
          outputMicrosPerMillion: out,
          fxMicros: fx,
          effectiveDate: price.date,
          priceSource: price.source,
        });
        if (!controller.signal.aborted) {
          setCatalog((list) => {
            const recent = [
              saved,
              ...list.filter((p) => p.id !== saved.id),
            ].slice(0, 20);
            // Draft selections must remain visible until the administrator changes or reloads them.
            const selected = list.filter(
              (p) =>
                (p.id === documentation || p.id === conversation) &&
                !recent.some((item) => item.id === p.id),
            );
            return [...recent, ...selected];
          });
          setPrice({ ...emptyPrice });
          setMessage(
            "Tarifa cadastrada. Selecione a versão desejada e salve a configuração.",
          );
        }
      };
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await operation();
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Erro ao salvar configuração.",
        );
        if (!(failure instanceof ProviderError) || failure.status !== 422)
          setRequiresReload(true);
      }
    } finally {
      if (controllerRef.current === controller && !controller.signal.aborted) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }
  const options = new Map(catalog.map((p) => [p.id, p]));
  if (state)
    Object.values(state.selectedTariffs).forEach((p) => {
      if (p) options.set(p.id, p);
    });
  const disabled = saving || requiresReload;
  const tariffSelect = (
    id: string,
    label: string,
    value: string,
    set: (value: string) => void,
  ) => (
    <OktonSelect
      name={id}
      label={label}
      value={value}
      onChange={(e) => set(e.target.value)}
      isDisabled={disabled}
    >
      <option value="">Não selecionado</option>
      {Array.from(options.values()).map((p) => (
        <option key={p.id} value={p.id}>
          {p.model} — {p.effectiveDate} — câmbio{" "}
          {(p.fxMicros / 1000000).toFixed(6)} — {p.id}
        </option>
      ))}
    </OktonSelect>
  );
  const inputField = (
    id: string,
    label: string,
    value: string,
    set: (value: string) => void,
  ) => (
    <OktonInput
      name={id}
      label={label}
      value={value}
      isDisabled={disabled}
      maxLength={2048}
      onChange={(e) => set(e.target.value)}
    />
  );
  const priceLabels: Record<keyof PriceFields, string> = {
    model: "Modelo exato",
    input: "Entrada (USD por milhão de tokens)",
    cache: "Entrada em cache (USD por milhão de tokens)",
    output: "Saída (USD por milhão de tokens)",
    fx: "Conversão (R$ por USD)",
    date: "Data da tarifa (AAAA-MM-DD)",
    source: "Fonte oficial da tarifa (HTTPS)",
  };
  return (
    <Box borderWidth="1px" rounded="md" p="5" mb="6">
      <Heading size="sm" mb="3">
        OpenAI — modelos e limites
      </Heading>
      <OktonAlert status="info" mb="3">
        <Text>
          {state?.executionStatus === "ready_for_sample"
            ? "Preparado para amostra sintética. Este status não confirma uma conexão testada. Cada etapa exige autorização atual."
            : "Provedor ainda não conectado. Esta configuração prepara o uso futuro e não envia chamadas à OpenAI."}
        </Text>
      </OktonAlert>
      <Text fontSize="sm" mb="3">
        Limites por solicitação complementam o teto global do piloto. Zero
        bloqueia o processamento, nunca significa uso ilimitado. Alterações não
        reiniciam o consumo.
      </Text>
      {error && (
        <OktonAlert status="error" mb="3">
          <Text>{error}</Text>
        </OktonAlert>
      )}
      {requiresReload && (
        <OktonAlert status="warning" mb="3">
          <Text>
            Recarregue para conferir o estado confirmado no servidor antes de
            gravar novamente.
          </Text>
        </OktonAlert>
      )}
      {message && (
        <OktonAlert status="success" mb="3">
          <Text>{message}</Text>
        </OktonAlert>
      )}
      {loading ? (
        <OktonLoadingState
          title="Carregando configuração da OpenAI..."
          minH="120px"
        />
      ) : (
        state && (
          <Stack spacing="4">
            <SimpleGrid columns={[1, 2]} spacing="3">
              {tariffSelect(
                "ai-documentation-tariff",
                "Modelo para documentação",
                documentation,
                setDocumentation,
              )}
              {tariffSelect(
                "ai-conversation-tariff",
                "Modelo para consultas",
                conversation,
                setConversation,
              )}
              {inputField(
                "ai-request-limit",
                "Limite por solicitação (R$)",
                limit,
                setLimit,
              )}
              {inputField(
                "ai-max-calls",
                "Máximo de chamadas por solicitação",
                calls,
                setCalls,
              )}
              {inputField(
                "ai-max-output",
                "Máximo de tokens de saída por chamada",
                output,
                setOutput,
              )}
            </SimpleGrid>
            <Box>
              <Button
                size="sm"
                colorScheme="blue"
                isDisabled={disabled}
                onClick={() => write("settings")}
              >
                Salvar configuração
              </Button>
            </Box>
            <Heading size="xs">Cadastrar versão de tarifa</Heading>
            <Text fontSize="sm">
              Valores em USD por milhão de tokens e câmbio administrativo, com
              até seis casas decimais. Confira a fonte oficial antes de
              cadastrar. A conversão não representa a fatura da OpenAI. Versões
              anteriores são preservadas; uma nova tarifa não altera os modelos
              selecionados.
            </Text>
            <SimpleGrid columns={[1, 2]} spacing="3">
              {(Object.keys(priceLabels) as Array<keyof PriceFields>).map(
                (key) => (
                  <Box key={key}>
                    {inputField(
                      "ai-price-" + key,
                      priceLabels[key],
                      price[key],
                      (value) =>
                        setPrice((previous) => ({ ...previous, [key]: value })),
                    )}
                  </Box>
                ),
              )}
            </SimpleGrid>
            <Box>
              <Button
                size="sm"
                isDisabled={disabled}
                onClick={() => write("price")}
              >
                Cadastrar tarifa
              </Button>
            </Box>
          </Stack>
        )
      )}
      {!loading && accessToken && (
        <Box mt="4">
          <Text fontSize="sm" mb="2">
            Recarregar substitui os campos pelos valores confirmados e limpa o
            cadastro de tarifa.
          </Text>
          <Button
            size="sm"
            isDisabled={saving}
            onClick={() => setReload((v) => v + 1)}
          >
            Recarregar configuração
          </Button>
        </Box>
      )}
    </Box>
  );
};
